import '.d{display:flex;}.ssr-plugin'
import '.r{align-items:center;}.ssr-plugin'
import '.s{gap:0.5rem;}.ssr-plugin'
import '.ck{border-radius:0.75rem;}.ssr-plugin'
import '.cl{font-size:1.125rem;}.ssr-plugin'
import '.cm{height:1.75rem;}.ssr-plugin'
import '.ah{flex-grow:1;}.ssr-plugin'
import '.l{padding:0px;}.ssr-plugin'
import '.al{border:none;}.ssr-plugin'
import '.cn{outline:none;}.ssr-plugin'
import '.co{background:transparent;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { type IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  useCallback,
  KeyboardEvent,
  MutableRefObject,
  forwardRef,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
  type ComponentProps,
} from 'react'
import { InputField } from '../InputField'
import { FileSelector, FileSelectorProps } from './FileSelector'

export type TextInputProps = {
  text: string
  placeholder?: string
  label?: string
  labelStyle?: Style
  required?: boolean
  disabled?: boolean
  errorMessage?: string
  icon?: IconDefinition
  submitIcon?: IconDefinition
  fileSelector?: FileSelectorProps
  endAdornment?: ReactNode
  style?: Style
  containerStyle?: Style
  ref?: MutableRefObject<HTMLInputElement>
  setText: (text: string) => void
  onSubmit?: () => void
  onFocus?: FocusEventHandler<HTMLInputElement>
  onBlur?: FocusEventHandler<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  inputType?: ComponentProps<'input'>['type']
  inputMode?: ComponentProps<'input'>['inputMode']
  min?: number
  max?: number
  maxLength?: number
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      text,
      placeholder,
      label,
      labelStyle,
      required,
      disabled,
      errorMessage,
      icon,
      submitIcon,
      fileSelector,
      endAdornment,
      style,
      containerStyle,
      setText,
      onSubmit,
      onFocus,
      onBlur,
      onKeyDown,
      inputType,
      inputMode,
      min,
      max,
      maxLength,
    }: TextInputProps,
    ref,
  ) => {
    const errorStateOn = !disabled && !!errorMessage
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (onKeyDown) {
        onKeyDown(event)
      }
      if (onSubmit && event.key === 'Enter') {
        onSubmit()
      }
    }
    const border = useCallback(
      (color: string) =>
        `1px solid var(${errorStateOn ? '--brand-primary' : color})`,
      [errorStateOn],
    )

    return (
      <InputField
        label={label}
        labelStyle={labelStyle}
        required={required}
        errorMessage={errorStateOn ? errorMessage : undefined}
        style={{
          ...style,
        }}
      >
        <styled.div className="d r s ck"
          style={{
            padding: icon ? '0.5rem 0.69rem' : '0.5rem 1rem',
            border: border('--palette-blue-300'),
            '&:hover': {
              border: border('--palette-blue-500'),
            },
            '&:focus-within': {
              border: border('--brand-secondary'),
            },
            '&:has(:disabled)': {
              border: 'var(--border-default)',
              background: 'var(--gray-100)',
            },
            background: errorStateOn
              ? 'var(--status-error-muted)'
              : 'var(--palette-white)',
            ...containerStyle,
          }}
        >
          {icon && (
            <FontAwesomeIcon
              icon={icon}
              style={{
                color: errorStateOn
                  ? 'var(--status-error)'
                  : 'var(--palette-blue-200)',
                height: '1.5rem',
                width: '1.5rem',
                marginRight: '0.24rem',
              }}
            />
          )}
          <styled.input className="cl cm ah l al cn co"
            type={inputType ?? 'text'}
            inputMode={inputMode}
            min={min}
            max={max}
            maxLength={maxLength}
            placeholder={placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            ref={ref}
            style={{
              '&::placeholder': {
                color: errorStateOn ? 'var(--status-error)' : 'var(--gray-300)',
                userSelect: 'none',
              },
              '&:disabled': {
                cursor: 'not-allowed',
              },
            }}
          ></styled.input>
          {endAdornment}
          {fileSelector && (
            <FileSelector
              {...fileSelector}
              iconStyle={{
                color: 'var(--palette-blue-200)',
                width: '1.125rem',
                height: '1.125rem',
                cursor: 'pointer',
                ...fileSelector.iconStyle,
              }}
            />
          )}
          {onSubmit && (
            <>
              <FontAwesomeIcon
                icon={submitIcon ?? faPaperPlaneTop}
                onClick={onSubmit}
                style={{
                  height: '1.125rem',
                  width: '1.125rem',
                  color: 'var(--palette-blue-200)',
                  cursor: 'pointer',
                }}
              />
            </>
          )}
        </styled.div>
      </InputField>
    )
  },
)

TextInput.displayName = 'TextInput'
